"use client";

import type { ComponentProps, FC, ReactNode } from "react";
import { cn } from "../../utils";
import { TypographyH2, TypographyH4, TypographyText } from "../Typography";
import PromoBannerCountdown from "./PromoBannerCountdown";
import useCountdownSeconds from "./useCountdownSeconds";

type Props = Omit<ComponentProps<"div">, "title"> & {
  banner?: string;
  bannerForeground?: string; // There's 2 layer on the banner, fg and bg.
  bannerForegroundMobile?: string; // Overrides bannerForeground below the md breakpoint.
  timeRemains?: number; // Number of seconds missing
  title: string;
  description?: string;
  infoButton?: ReactNode;
  fgCenter?: boolean;
  isHomepage?: boolean;
  /* Following fields are Deprecated 17/08/2026 */
  haveInfoIcon?: boolean; // TODO: Remove
  haveTrophyIcon?: boolean; // TODO: Remove
  haveButton?: boolean; // TODO: Remove
  onInfoIcon?: () => void; // TODO: Remove
  onTrophyIcon?: () => void; // TODO: Remove
};

const PromoBanner: FC<Props> = ({
  title,
  description,
  banner,
  bannerForeground,
  bannerForegroundMobile,
  timeRemains,
  className,
  style,
  children,
  infoButton,
  fgCenter,
  isHomepage,
  haveInfoIcon: _haveInfoIcon,
  haveTrophyIcon: _haveTrophyIcon,
  haveButton: _haveButton,
  onInfoIcon: _onInfoIcon,
  onTrophyIcon: _onTrophyIcon,
  ...props
}) => {
  const secondsLeft = useCountdownSeconds(timeRemains);

  if (!title) return null;

  const background = banner ? { backgroundImage: `url(${banner})` } : undefined;
  const TitleComp = isHomepage ? TypographyH2 : TypographyH4;
  return (
    <div
      {...props}
      className={cn(
        "w-full h-full flex max-md:flex-col gap-2 max-md:pr-10 relative justify-start md:justify-between items-center md:items-center md:min-h-20 py-4 rounded-lg",
        !banner && "bg-primary",
        isHomepage ? "px-4" : "px-2",
        className,
      )}
      style={style}
    >
      {background && (
        <div
          aria-hidden="true"
          data-slot="promo-banner-background"
          className="absolute inset-0 z-0 bg-cover bg-right bg-position-right rounded-lg rtl:-scale-x-100"
          style={background}
        />
      )}
      {bannerForeground && !isHomepage && (
        <img
          src={bannerForeground}
          className={cn(
            "h-full z-0 top-0 absolute w-auto! max-w-none!",
            fgCenter
              ? "right-1/2 transform translate-x-1/2"
              : "ltr:right-0 rtl:left-0",
            bannerForegroundMobile && "max-md:hidden",
          )}
        />
      )}
      {bannerForegroundMobile && !isHomepage && (
        <img
          src={bannerForegroundMobile}
          className="h-full z-0 ltr:right-0 rtl:left-0 top-0 absolute w-auto! max-w-none! max-md:block md:hidden"
        />
      )}
      <div className="w-full z-10 flex flex-col z-1">
        <TitleComp
          className={cn(
            "w-full text-white text-white! tracking-wide pl-1 font-semibold",
            isHomepage && "max-w-[60%] max-sm:text-xl! z-10 relative mb-0!",
          )}
        >
          {title}
        </TitleComp>
        {description && (
          <TypographyText
            as="p"
            variant={isHomepage ? "default" : "small"}
            className={cn(
              "text-white! mt-[1]! pl-1 font-semibold",
              isHomepage && "hidden md:block max-w-[300px]",
            )}
          >
            {description}
          </TypographyText>
        )}
        <div
          className={cn(
            "absolute ltr:right-4 rtl:left-4 m-0! gap-x-2  items-center",
            fgCenter ? "top-1/2 transform -translate-y-1/2" : "top-4",
            isHomepage
              ? "flex-row-reverse gap-x-4 hidden xl:flex! h-full"
              : "hidden 2xl:flex!",
          )}
        >
          {fgCenter && children}
          {fgCenter && timeRemains && (
            <>
              <div className="mt-3" />
              <PromoBannerCountdown secondsLeft={secondsLeft} />
            </>
          )}
          {infoButton}
        </div>
        {fgCenter && (
          <div
            className={cn(
              !isHomepage && "px-1",
              "flex items-center w-full mt-2 gap-x-3 max-sm:flex-wrap",
              isHomepage
                ? "block xl:hidden! justify-start gap-x-4"
                : "block 2xl:hidden! justify-between",
            )}
          >
            {children}
            {!isHomepage && <div className="flex flex-1" />}
            <PromoBannerCountdown secondsLeft={secondsLeft} />
          </div>
        )}
        {!fgCenter && (
          <div className="flex justify-end w-full mt-2 px-1">
            {children}
            <div className="flex flex-1" />
            <PromoBannerCountdown secondsLeft={secondsLeft} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PromoBanner;
